/**
 * Real-time countdown derived from an absolute end time.
 * Uses dual scheduling: requestAnimationFrame for visible tabs, setInterval for hidden tabs.
 * Always computes remaining time from absolute deadline to prevent drift and handle sleep/wake.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Real-time countdown hook that derives seconds from an absolute deadline
 * @param {Object} config - Configuration object
 * @param {number} config.endAtMs - Epoch ms when countdown should hit 0
 * @param {Function} config.onEnd - Called once when remaining <= 0
 * @param {number} config.visibleUpdateMs - Soft minimum update cadence while visible (via rAF loop)
 * @param {number} config.hiddenUpdateMs - Interval cadence when tab is hidden
 * @returns {number} Current remaining seconds
 */
export default function useRealTimeCountdown({ 
  endAtMs, 
  onEnd, 
  visibleUpdateMs = 250, 
  hiddenUpdateMs = 1000 
}) {
  const [seconds, setSeconds] = useState(() => initialSeconds(endAtMs));
  const rafRef = useRef(null);
  const intervalRef = useRef(null);
  const endedRef = useRef(false);

  // Compute remaining seconds from absolute deadline and update state
  const computeAndSet = useCallback(() => {
    // If endAtMs is null/undefined, don't compute anything
    if (!endAtMs) {
      return;
    }
    
    const remainingMs = Math.max(0, endAtMs - Date.now());
    const next = Math.ceil(remainingMs / 1000);
    
    // Only update state if the value actually changed
    setSeconds(prev => (prev !== next ? next : prev));
    
    // Call onEnd exactly once when time reaches 0
    if (!endedRef.current && remainingMs <= 0) {
      endedRef.current = true;
      onEnd?.();
    }
  }, [endAtMs, onEnd]);

  // Start smooth animation loop for visible tabs using requestAnimationFrame
  const startVisibleLoop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    let last = 0;
    
    const step = (ts) => {
      if (!document.hidden) {
        // Throttle to ~visibleUpdateMs using timestamp deltas
        if (!last || ts - last >= visibleUpdateMs) {
          computeAndSet();
          last = ts;
        }
        rafRef.current = requestAnimationFrame(step);
      }
    };
    
    rafRef.current = requestAnimationFrame(step);
  }, [computeAndSet, visibleUpdateMs]);

  // Start interval loop for hidden tabs
  const startHiddenLoop = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(computeAndSet, hiddenUpdateMs);
  }, [computeAndSet, hiddenUpdateMs]);

  // Stop all scheduling loops
  const stopAll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    clearInterval(intervalRef.current);
    rafRef.current = null;
    intervalRef.current = null;
  }, []);

  // Handle visibility changes and start appropriate scheduling loop
  const handleVisibilityChange = useCallback(() => {
    stopAll();
    if (document.hidden) {
      startHiddenLoop();
    } else {
      startVisibleLoop();
    }
  }, [stopAll, startHiddenLoop, startVisibleLoop]);

  // Initialize countdown and set up visibility handling
  useEffect(() => {
    // Only start if we have a valid endAtMs
    if (endAtMs) {
      // Initialize immediately so UI reflects the current remaining time
      computeAndSet();
      
      // Start appropriate loop based on current visibility
      handleVisibilityChange();
    } else {
      // Stop all loops if no valid endAtMs
      stopAll();
    }
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopAll();
    };
  }, [endAtMs, computeAndSet, handleVisibilityChange, stopAll]);

  // Re-sync if endAtMs changes (e.g., new deadline set)
  useEffect(() => {
    // Reset ended flag when endAtMs changes
    endedRef.current = false;
    if (endAtMs) {
      computeAndSet();
    }
  }, [endAtMs, computeAndSet]);

  return seconds;
}

/**
 * Calculate initial seconds from endAtMs
 * @param {number} endAtMs - Epoch ms when countdown should hit 0
 * @returns {number} Initial remaining seconds
 */
function initialSeconds(endAtMs) {
  if (!endAtMs) {
    return 0;
  }
  const ms = Math.max(0, endAtMs - Date.now());
  return Math.ceil(ms / 1000);
}
